<template>
  <div class="rc">

    <!-- ══ FACTURAS EMITIDAS (arriba, antes de los cargos) ═════════════════ -->
    <EmittedInvoicesList
      v-if="canInvoice && emittedInvoices.length"
      :invoices="emittedWithConcepts"
      :can-invoice="canInvoice"
      @whatsapp="i => $refs.invoicePanel && $refs.invoicePanel.openWhatsappConfirmModal(i)"
      @correct="i => $refs.invoicePanel && $refs.invoicePanel.openCorrectionModal(i)"
      @void="i => $refs.invoicePanel && $refs.invoicePanel.openVoidModal(i)"
    />

    <!-- Sección de cargos: se COLAPSA tras emitir la 1ª factura (el panel muestra el
         botón "Crear otra factura para esta reserva"); reaparece al crear un borrador. -->
    <div v-show="showChargesSection" class="rc-charges-section">
    <!-- ══ HEADER ══════════════════════════════════════════════════════════ -->
    <div class="rc-header">
      <div class="rc-header__left">
        <p class="rc-header__title">{{ lodgingBilled ? $t('bookingCharges.header.titleNew') : $t('bookingCharges.header.title') }}</p>
        <p class="rc-header__sub">
          {{ lodgingBilled
            ? $t('bookingCharges.header.subNew')
            : $t('bookingCharges.header.sub') }}
        </p>
      </div>
      <button class="rc-btn-add" @click="openChargeModal()" :disabled="loading">
        <i class="fa-solid fa-plus"></i>
        {{ $t('bookingCharges.buttons.addCharge') }}
      </button>
    </div>

    <!-- ══ LOADING ══════════════════════════════════════════════════════════ -->
    <div v-if="loading" class="rc-loading">
      <span class="rc-spinner"></span>
      <span>{{ $t('bookingCharges.loading') }}</span>
    </div>

    <template v-else>

      <!-- ══ EMPTY ════════════════════════════════════════════════════════ -->
      <div v-if="!hasDisplayRows" class="rc-empty">
        <div class="rc-empty__icon">
          <i :class="lodgingBilled ? 'fa-solid fa-circle-check' : 'fa-solid fa-receipt'"></i>
        </div>
        <div>
          <p class="rc-empty__title">{{ lodgingBilled ? $t('bookingCharges.empty.titleBilled') : $t('bookingCharges.empty.title') }}</p>
          <p class="rc-empty__sub">
            {{ lodgingBilled
              ? $t('bookingCharges.empty.subBilled')
              : $t('bookingCharges.empty.sub') }}
          </p>
        </div>
        <button class="rc-btn-add rc-btn-add--ghost" @click="openChargeModal()">
          <i class="fa-solid fa-plus"></i>
          {{ lodgingBilled ? $t('bookingCharges.buttons.addCharge') : $t('bookingCharges.buttons.addFirstCharge') }}
        </button>
      </div>

      <!-- ══ TABLA DE CARGOS ══════════════════════════════════════════════ -->
      <div v-if="hasDisplayRows" class="rc-table">

        <!-- Cabecera de tabla -->
        <div class="rc-table__head">
          <span class="rth-desc">{{ $t('bookingCharges.table.item') }}</span>
          <span class="rth-val">{{ $t('bookingCharges.table.qty') }}</span>
          <span class="rth-val">{{ $t('bookingCharges.table.unitPrice') }}</span>
          <span class="rth-val">{{ $t('bookingCharges.table.vat') }}</span>
          <span class="rth-val rth-val--total">{{ $t('bookingCharges.table.total') }}</span>
          <span class="rth-actions"></span>
        </div>

        <!-- Filas base (alojamiento/descuentos/automáticos): solo hasta emitir la 1ª factura.
             Después se muestran como detalle dentro de esa factura, arriba. -->
        <template v-if="showBaseRows">
        <!-- ── Filas de alojamiento (virtuales) ─────────────────────────── -->
        <div v-for="(vRow, idx) in virtualRows" :key="'v_' + idx" class="rc-row rc-row--lodging">
          <div class="rc-row__desc">
            <!-- El TIPO arriba y los números en su propia etiqueta: juntos en una
                 sola línea se cortaban («Hab. Simple · H1 – Ha…») y no se sabía de
                 qué habitación era el cargo. -->
            <span class="rc-row__name">{{ vRow.description }}</span>
            <div class="rc-row__meta">
              <span class="rc-chip rc-chip--blue">{{ $t('bookingCharges.chips.lodging') }}</span>
              <span v-for="code in (vRow.room_codes || [])" :key="code" class="rc-chip rc-chip--room">{{ code }}</span>
            </div>
            <!-- Qué se está cobrando, en una línea: «2 noches × 2 habitaciones». -->
            <div class="rc-row__meta rc-row__meta--sub">
              <span class="rc-row__date">{{ vRow.date }}</span>
              <span v-if="vRow.nights" class="rc-row__stay">
                {{ $tc('bookingCharges.stay.nights', vRow.nights, { n: vRow.nights }) }}
                <template v-if="vRow.rooms > 1">
                  · {{ $tc('bookingCharges.stay.rooms', vRow.rooms, { n: vRow.rooms }) }}
                </template>
              </span>
            </div>
          </div>
          <span class="rc-row__val">{{ vRow.quantity }}</span>
          <span class="rc-row__val">
            <span class="rc-price">
              <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(vRow.unit_price).showCode}">{{ fmtParts(vRow.unit_price).code }}</sup>{{ fmtParts(vRow.unit_price,true).amount }}
            </span>
          </span>
          <span class="rc-row__val"><span class="rc-vat">{{ vRow.vat_rate }}%</span></span>
          <span class="rc-row__val rc-row__val--total">
            <span class="rc-total">
              <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(vRow.total_with_vat).showCode}">{{ fmtParts(vRow.total_with_vat).code }}</sup>{{ fmtParts(vRow.total_with_vat,true).amount }}
            </span>
          </span>
          <span class="rc-row__actions">
            <span class="rc-lock-ico">
              <span class="rc-icon-btn rc-icon-btn--lock">
                <i class="fa-solid fa-lock"></i>
              </span>
            </span>
          </span>
        </div>

        <!-- ── Descuentos (de financial_lines) ──────────────────────────── -->
        <div v-for="(dRow, idx) in discountRows" :key="'d_' + idx" class="rc-row rc-row--discount">
          <div class="rc-row__desc">
            <span class="rc-row__name rc-row__name--discount">{{ dRow.description }}</span>
            <div class="rc-row__meta">
              <span class="rc-chip rc-chip--green">{{ $t('bookingCharges.chips.discount') }}</span>
              <span v-if="dRow.date" class="rc-row__date">{{ formatDate(dRow.date) }}</span>
            </div>
          </div>
          <span class="rc-row__val">—</span>
          <span class="rc-row__val">—</span>
          <span class="rc-row__val">—</span>
          <span class="rc-row__val rc-row__val--total">
            <span class="rc-total rc-total--discount">
              <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(dRow.total_with_vat).showCode}">{{ fmtParts(dRow.total_with_vat).code }}</sup>{{ fmtParts(dRow.total_with_vat, true).amount }}
            </span>
          </span>
          <span class="rc-row__actions">
            <span class="rc-lock-ico">
              <span class="rc-icon-btn rc-icon-btn--lock">
                <i class="fa-solid fa-lock"></i>
              </span>
            </span>
          </span>
        </div>

        <!-- ── Cargos automáticos (de financial_lines) ─────────────────── -->
        <div v-for="(aRow, idx) in automaticChargeRows" :key="'a_' + idx" class="rc-row rc-row--auto">
          <div class="rc-row__desc">
            <span class="rc-row__name">{{ aRow.description }}</span>
            <div class="rc-row__meta">
              <span v-if="aRow.category" class="rc-chip rc-chip--violet">{{ aRow.category }}</span>
              <span v-if="aRow.chargeBasis" class="rc-chip rc-chip--blue">{{ aRow.chargeBasis }}</span>
              <span v-if="aRow.charge_date" class="rc-row__date">{{ formatDate(aRow.charge_date) }}</span>
              <span class="rc-row__note">{{ $t('bookingCharges.chips.autoSynced') }}</span>
            </div>
          </div>
          <span class="rc-row__val">{{ aRow.quantity }}</span>
          <span class="rc-row__val">
            <span class="rc-price">
              <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(aRow.unit_price).showCode}">{{ fmtParts(aRow.unit_price).code }}</sup>{{ fmtParts(aRow.unit_price,true).amount }}
            </span>
          </span>
          <span class="rc-row__val"><span class="rc-vat">{{ aRow.vat_rate }}%</span></span>
          <span class="rc-row__val rc-row__val--total">
            <span class="rc-total">
              <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(aRow.total_with_vat).showCode}">{{ fmtParts(aRow.total_with_vat).code }}</sup>{{ fmtParts(aRow.total_with_vat,true).amount }}
            </span>
          </span>
          <span class="rc-row__actions">
            <span class="rc-lock-ico">
              <span class="rc-icon-btn rc-icon-btn--lock">
                <i class="fa-solid fa-lock"></i>
              </span>
            </span>
          </span>
        </div>

        </template>

        <!-- ── Cargos manuales (pendientes / del borrador en curso) ──────── -->
        <div
          v-for="charge in displayCharges"
          :key="charge.id"
          class="rc-row"
          :class="{ 'rc-row--billed': charge.is_billed }"
        >
          <div class="rc-row__desc">
            <span class="rc-row__name">{{ charge.description }}</span>
            <div class="rc-row__meta">
              <span v-if="charge.room_name" class="rc-chip rc-chip--blue">{{ charge.room_name }}</span>
              <span v-if="charge.category" class="rc-chip">{{ charge.category }}</span>
              <!-- <span v-if="charge.is_billed" class="rc-chip rc-chip--green">
                <i class="fa-solid fa-check" style="font-size:8px"></i>
                Facturado
              </span> -->
              <span class="rc-row__date">{{ formatDate(charge.charge_date) }}</span>
              <span v-if="charge.notes" class="rc-row__note">{{ charge.notes }}</span>
            </div>
          </div>
          <span class="rc-row__val">{{ charge.quantity }}</span>
          <span class="rc-row__val">
            <span class="rc-price">
              <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(charge.unit_price).showCode}">{{ fmtParts(charge.unit_price).code }}</sup>{{ fmtParts(charge.unit_price,true).amount }}
            </span>
          </span>
          <span class="rc-row__val"><span class="rc-vat">{{ charge.vat_rate }}%</span></span>
          <span class="rc-row__val rc-row__val--total">
            <span class="rc-total">
              <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(charge.total_with_vat).showCode}">{{ fmtParts(charge.total_with_vat).code }}</sup>{{ fmtParts(charge.total_with_vat,true).amount }}
            </span>
          </span>
          <span class="rc-row__actions">
            <!-- FIX: Solo mostrar acciones si NO está facturado -->
            <template v-if="!charge.is_billed">
              <button class="rc-icon-btn rc-icon-btn--edit" @click="openChargeModal(charge)" :title="$t('bookingCharges.buttons.editTitle')">
                <i v-if="deleting !== charge.id" class="fa-solid fa-pen-to-square"></i>
              </button>
              <button class="rc-icon-btn rc-icon-btn--del" @click="pendingDelete = charge" :disabled="deleting === charge.id" :title="$t('bookingCharges.buttons.deleteTitle')">
                <i v-if="deleting === charge.id" class="fa-solid fa-circle-notch fa-spin"></i>
                <i v-else class="fa-solid fa-trash-can"></i>
              </button>
            </template>
            <span v-else class="rc-lock-ico">
              <span class="rc-icon-btn rc-icon-btn--lock">
                <i class="fa-solid fa-lock"></i>
              </span>
            </span>
          </span>
        </div>

        <!-- Footer: total (a pagar, o pendiente de facturar tras la 1ª emisión) -->
        <div class="rc-table__foot">
          <span class="rc-foot__label">{{ lodgingBilled ? $t('bookingCharges.footer.pending') : $t('bookingCharges.footer.totalDue') }}</span>
          <span class="rc-foot__total">
            <sup class="rc-cur rc-cur--footer" :class="{'rc-cur--code':fmtParts(footerTotal).showCode}">{{ fmtParts(footerTotal).code }}</sup>{{ fmtParts(footerTotal,false).amount }}
          </span>
        </div>

      </div>

    </template>
    </div>
    <!-- /rc-charges-section -->

    <!-- ══ PANEL FACTURA ══════════════════════════════════════════════════ -->
<div v-if="canInvoice" class="rc-invoice-section">
  <div class="rc-invoice-label">
    {{ $t('bookingCharges.invoice.sectionLabel') }}
  </div>
  <ReservationInvoicePanel
    ref="invoicePanel"
    :reservationData="reservationData"
    :hide-emitted-list="true"
    :draft-concepts="draftConcepts"
    @emitted-changed="emittedInvoices = $event"
    @state-changed="activeInvoice = $event"
    @charges-updated="onInvoiceVoided"
    @invoice-created="onInvoiceCreated"
  />
</div>

    <!-- ══ MODAL: Añadir / Editar cargo ════════════════════════════════════ -->
    <transition name="rc-fade">
      <div v-if="chargeModal.open" class="rc-overlay" @click.self="closeChargeModal">
        <div class="rc-modal rc-modal--charge">

          <div class="rc-modal__head">
            <div class="rc-modal__icon">
              <i class="fa-solid fa-file-lines"></i>
            </div>
            <div>
              <h3 class="rc-modal__title">{{ chargeModal.isNew ? $t('bookingCharges.buttons.addCharge') : $t('bookingCharges.modal.editTitle') }}</h3>
              <p class="rc-modal__sub">{{ chargeModal.isNew ? $t('bookingCharges.modal.addSub') : $t('bookingCharges.modal.editSub') }}</p>
            </div>
            <button class="rc-modal__x" @click="closeChargeModal" :disabled="saving">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="rc-modal__body">

            <!-- Artículo + dropdown -->
            <div class="mf-group mf-group--full" ref="descGroup">
              <label class="mf-label">{{ $t('bookingCharges.table.item') }} <span class="req">*</span></label>
              <div class="mf-row-wrap">
                <input
                  v-model="chargeModal.form.description"
                  type="text"
                  class="mf-input"
                  :placeholder="$t('bookingCharges.placeholders.item')"
                  @focus="openDropdown"
                  @input="onDescInput"
                  autocomplete="off"
                  ref="descInput"
                />
                <button class="mf-gear" @mousedown.prevent="openManageModal" :title="$t('bookingCharges.buttons.manageTemplatesTitle')">
                  <i class="fa-solid fa-gear"></i>
                </button>
              </div>
              <!-- Dropdown plantillas -->
              <div v-if="showDropdown" class="art-dd">
                <div class="art-dd__head">
                  <span class="art-dd__ttl">{{ $t('bookingCharges.templates.savedTitle') }}</span>
                  <button class="art-dd__manage" @mousedown.prevent="openManageModal">
                    <i class="fa-solid fa-gear"></i>
                    {{ $t('bookingCharges.buttons.manage') }}
                  </button>
                </div>
                <div class="art-dd__sep"></div>
                <div v-if="!filteredSaved.length" class="art-dd__empty">
                  <span v-if="descSearch">{{ $t('bookingCharges.templates.noResults', { query: descSearch }) }}</span>
                  <span v-else>{{ $t('bookingCharges.templates.emptyPre') }}<span class="art-dd__link" @mousedown.prevent="openManageModal">{{ $t('bookingCharges.templates.createOne') }}</span></span>
                </div>
                <div v-for="group in filteredSaved" :key="group.category" class="art-dd__group">
                  <div class="art-dd__cat">{{ group.category }}</div>
                  <div v-for="tpl in group.items" :key="tpl.id" class="art-dd__item" @mousedown.prevent="applyTemplate(tpl)">
                    <span class="art-dd__item-name">{{ tpl.name }}</span>
                    <div class="art-dd__item-right">
                      <span v-if="tpl.default_vat > 0" class="art-dd__vat">{{ $t('bookingCharges.vat.labelPct', { rate: tpl.default_vat }) }}</span>
                      <span class="art-dd__price">{{ fmtParts(tpl.default_price).code }} {{ fmtParts(tpl.default_price,false).amount }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Fecha + Categoría -->
            <div class="mf-grid mf-grid--2">
              <div class="mf-group">
                <label class="mf-label">{{ $t('bookingCharges.fields.date') }} <span class="req">*</span></label>
                <input v-model="chargeModal.form.charge_date" type="date" class="mf-input" />
              </div>
              <div class="mf-group">
                <label class="mf-label">{{ $t('bookingCharges.fields.category') }}</label>
                <input v-model="chargeModal.form.category" type="text" class="mf-input" :placeholder="$t('bookingCharges.placeholders.egCleaning')" list="charge-cat-list" />
                <datalist id="charge-cat-list">
                  <option v-for="c in categoryOptions" :key="c" :value="c"/>
                </datalist>
              </div>
            </div>

            <!-- Cantidad + Precio + IVA -->
            <div class="mf-grid mf-grid--3">
              <div class="mf-group">
                <label class="mf-label">{{ $t('bookingCharges.fields.quantity') }} <span class="req">*</span></label>
                <input v-model.number="chargeModal.form.quantity" type="number" min="1" class="mf-input" />
              </div>
              <div class="mf-group">
                <label class="mf-label">{{ $t('bookingCharges.fields.unitPriceFull') }} <span class="req">*</span></label>
                <input v-model.number="chargeModal.form.unit_price" type="number" min="0" step="0.01" class="mf-input" />
              </div>
              <div class="mf-group">
                <label class="mf-label">{{ $t('bookingCharges.fields.vatPct') }}</label>
                <input v-model.number="chargeModal.form.vat_rate" type="number" min="0" max="100" step="0.1" class="mf-input" />
              </div>
            </div>

            <!-- Preview total -->
            <div class="mf-preview">
              <span class="mf-preview__lbl">{{ $t('bookingCharges.modal.totalCalculated') }}</span>
              <span class="mf-preview__val">
                <sup class="rc-cur" :class="{'rc-cur--code':fmtParts(chargePreviewTotal).showCode}">{{ fmtParts(chargePreviewTotal).code }}</sup>{{ fmtParts(chargePreviewTotal,false).amount }}
              </span>
            </div>

            <!-- De qué habitación es. Solo en reservas de varias: con una sola no
                 significa nada y estorba. -->
            <div v-if="habitaciones.splittable" class="mf-group mf-group--full">
              <label class="mf-label">
                {{ $t('bookingCharges.fields.room') }}
                <span class="mf-opt">{{ $t('bookingCharges.common.optional') }}</span>
              </label>
              <select v-model="chargeModal.form.room_instance_id" class="mf-input">
                <option :value="null">{{ $t('bookingCharges.fields.roomAll') }}</option>
                <option v-for="h in habitaciones.rooms" :key="h.id" :value="h.id">{{ h.label }}</option>
              </select>
              <span class="mf-hint">{{ $t('bookingCharges.fields.roomHint') }}</span>
            </div>

            <!-- Notas -->
            <div class="mf-group mf-group--full">
              <label class="mf-label">{{ $t('bookingCharges.fields.internalNotes') }} <span class="mf-opt">{{ $t('bookingCharges.common.optional') }}</span></label>
              <input v-model="chargeModal.form.notes" type="text" class="mf-input" :placeholder="$t('bookingCharges.placeholders.internalNote')" />
            </div>

          </div>

          <div class="rc-modal__foot">
            <button class="rc-mbtn rc-mbtn--cancel" @click="closeChargeModal" :disabled="saving">
              <i class="fa-solid fa-xmark"></i>
              {{ $t('bookingCharges.buttons.cancel') }}
            </button>
            <button class="rc-mbtn rc-mbtn--save" @click="saveCharge" :disabled="saving">
              <i v-if="saving" class="fa-solid fa-circle-notch fa-spin"></i>
              <i v-else class="fa-solid fa-check"></i>
              {{ saving ? $t('bookingCharges.buttons.saving') : (chargeModal.isNew ? $t('bookingCharges.buttons.saveCharge') : $t('bookingCharges.buttons.saveChanges')) }}
            </button>
          </div>

        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Gestionar plantillas ═════════════════════════════════════ -->
    <transition name="rc-fade">
      <div v-if="showManageModal" class="rc-overlay" @click.self="showManageModal = false">
        <div class="rc-modal">
          <div class="rc-modal__head">
            <div class="rc-modal__icon">
              <i class="fa-solid fa-gear"></i>
            </div>
            <div>
              <h3 class="rc-modal__title">{{ $t('bookingCharges.templates.manageTitle') }}</h3>
              <p class="rc-modal__sub">{{ $t('bookingCharges.templates.manageSub') }}</p>
            </div>
            <button class="rc-modal__x" @click="showManageModal = false">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="rc-tpl-form">
            <div class="rc-tpl-grid">
              <div class="rc-tf rc-tf--name"><label class="mf-label">{{ $t('bookingCharges.fields.name') }} <span class="req">*</span></label><input v-model="newTpl.name" type="text" class="mf-input" :placeholder="$t('bookingCharges.placeholders.egCleaning')" ref="newTplInput" /></div>
              <div class="rc-tf rc-tf--cat"><label class="mf-label">{{ $t('bookingCharges.fields.category') }}</label><input v-model="newTpl.category" type="text" class="mf-input" :placeholder="$t('bookingCharges.placeholders.egCleaning')" list="mnf-cat-list" /><datalist id="mnf-cat-list"><option v-for="c in categoryOptions" :key="c" :value="c"/></datalist></div>
              <div class="rc-tf rc-tf--price"><label class="mf-label">{{ $t('bookingCharges.fields.price') }}</label><input v-model.number="newTpl.default_price" type="number" min="0" step="0.01" class="mf-input" placeholder="0.00" /></div>
              <div class="rc-tf rc-tf--vat"><label class="mf-label">{{ $t('bookingCharges.fields.vatPct') }}</label><input v-model.number="newTpl.default_vat" type="number" min="0" max="100" step="0.1" class="mf-input" placeholder="0" /></div>
            </div>
            <button class="rc-mbtn rc-mbtn--save" @click="saveTpl" :disabled="savingTpl || !newTpl.name.trim()">
              <i v-if="savingTpl" class="fa-solid fa-circle-notch fa-spin"></i>
              <i v-else class="fa-solid fa-plus"></i>
              {{ $t('bookingCharges.buttons.add') }}
            </button>
          </div>

          <div class="rc-tpl-list">
            <div v-if="!allTemplates.length" class="rc-tpl-empty">{{ $t('bookingCharges.templates.emptyList') }}</div>
            <template v-else>
              <div v-for="group in templateGroups" :key="group.category" class="rc-tpl-group">
                <div class="rc-tpl-cat">{{ group.category || $t('bookingCharges.templates.noCategory') }}</div>
                <div v-for="tpl in group.items" :key="tpl.id" class="rc-tpl-item">
                  <div class="rc-tpl-item__info">
                    <span class="rc-tpl-item__name">{{ tpl.name }}</span>
                    <span class="rc-tpl-item__price">{{ fmtParts(tpl.default_price).code }} {{ fmtParts(tpl.default_price,false).amount }}</span>
                    <span v-if="tpl.default_vat > 0" class="rc-tpl-item__vat">{{ $t('bookingCharges.vat.labelPct', { rate: tpl.default_vat }) }}</span>
                  </div>
                  <button class="rc-tpl-del" @click="deleteTpl(tpl.id)" :disabled="deletingTpl === tpl.id">
                    <i v-if="deletingTpl === tpl.id" class="fa-solid fa-circle-notch fa-spin"></i>
                    <i v-else class="fa-solid fa-trash-can"></i>
                  </button>
                </div>
              </div>
            </template>
          </div>

          <div class="rc-modal__foot rc-modal__foot--end">
            <button class="rc-mbtn rc-mbtn--cancel" @click="showManageModal = false">
              <i class="fa-solid fa-xmark"></i>
              {{ $t('bookingCharges.buttons.close') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Confirmar eliminar cargo ══════════════════════════════════ -->
    <transition name="rc-fade">
      <div v-if="pendingDelete" class="rc-overlay" @click.self="pendingDelete = null">
        <div class="rc-modal rc-modal--sm">
          <div class="rc-modal__head">
            <div class="rc-modal__icon rc-modal__icon--danger">
              <i class="fa-solid fa-trash-can"></i>
            </div>
            <div>
              <h3 class="rc-modal__title">{{ $t('bookingCharges.deleteModal.title') }}</h3>
              <p class="rc-modal__sub">{{ $t('bookingCharges.deleteModal.sub') }}</p>
            </div>
            <button class="rc-modal__x" @click="pendingDelete = null">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div class="rc-modal__body rc-modal__body--sm">
            <p class="rc-del-name"><strong>{{ pendingDelete.description }}</strong></p>
            <p class="rc-del-meta">{{ fmt(pendingDelete.total_with_vat) }} · {{ formatDate(pendingDelete.charge_date) }}</p>
          </div>
          <div class="rc-modal__foot">
            <button class="rc-mbtn rc-mbtn--cancel" @click="pendingDelete = null">
              <i class="fa-solid fa-xmark"></i>
              {{ $t('bookingCharges.buttons.cancel') }}
            </button>
            <button class="rc-mbtn rc-mbtn--danger" @click="deleteCharge(pendingDelete)" :disabled="deleting === pendingDelete?.id">
              <i v-if="deleting === pendingDelete?.id" class="fa-solid fa-circle-notch fa-spin"></i>
              <i v-else class="fa-solid fa-trash-can"></i>
              {{ deleting === pendingDelete?.id ? $t('bookingCharges.buttons.deleting') : $t('bookingCharges.buttons.confirmDelete') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import toastr from 'toastr';
import ReservationInvoicePanel from '@/components/booking/ReservationInvoicePanel.vue';
import EmittedInvoicesList from '@/components/booking/EmittedInvoicesList.vue';
import { resolveReservationCurrency } from '@/utils/formatters';

const TODAY = () => moment().format('YYYY-MM-DD');
const BLANK_FORM = (currency = 'USD', vat = 0) => ({
  charge_date: TODAY(), description: '', category: '',
  quantity: 1, unit_price: 0, vat_rate: vat, notes: '',
  // De qué habitación es el cargo. Nulo = de toda la reserva.
  room_instance_id: null,
});
const BLANK_TPL = () => ({ name: '', category: '', default_price: 0, default_vat: 0 });

/**
 * Una NOTA DE CRÉDITO no factura nada: es el documento que anula una factura.
 *
 * Si contara como factura viva, después de anular el alojamiento seguiría dándose
 * por facturado, «Cargos nuevos» saldría vacío y no habría nada que volver a
 * emitir — que es justo lo que el anfitrión necesita hacer tras anular.
 */
const esFactura = (i) => ((i && i.type) || 'standard') !== 'credit-note';

export default {
  name: 'ReservationCharges',
  components: { ReservationInvoicePanel, EmittedInvoicesList },

  props: {
    reservationData: { type: Object, required: true },
  },

  data() {
    return {
      charges: [], allTemplates: [], templateGroups: [],
      loading: false, saving: false, deleting: null, savingTpl: false, deletingTpl: null,
      pendingDelete: null,
      showDropdown: false, descSearch: '',
      showManageModal: false, newTpl: BLANK_TPL(),
      categoryOptions: ['Alojamiento','Limpieza','Desayuno','Parking','Traslado','Extra','Daños','Tasa turística','Otro'],
      chargeModal: { open: false, isNew: true, editId: null, form: BLANK_FORM() },
      // Las habitaciones de la reserva, para poder decir de cuál es cada cargo.
      habitaciones: { splittable: false, rooms: [] },
      emittedInvoices: [],   // facturas ya emitidas (las publica el panel por evento)
      activeInvoice: null,   // factura en curso (borrador/emitiéndose) que publica el panel
    };
  },

  computed: {
    canInvoice() {
    const userRoot = this.$store.state.userRoot;
    // Multi-emisor: emisor aplicable a la reserva (asignado a su propiedad o el
    // principal). Fallback al perfil único antiguo por compatibilidad.
    const bp = this.reservationData?.billing_emisor || userRoot?.billing_profile || userRoot?.billingProfile;
    if (!bp || bp.is_active === false) return false;
    // El email del emisor NO es obligatorio para facturar (es opcional en GOBL).
    const required = ['country', 'legal_name', 'tax_id', 'address_street', 'address_city', 'address_region', 'address_zip'];
    const allFilled = required.every(k => {
      const v = bp[k];
      return v !== null && v !== undefined && String(v).trim() !== '';
    });
    return allFilled && userRoot?.servicios?.electronic_invoicing === true;
  },
    reservaId() { return this.reservationData?.id; },

    propertyCurrency() {
      // Moneda REAL por país (la columna currency de la propiedad no es fiable).
      return resolveReservationCurrency(
        this.reservationData,
        this.$store?.state?.property?.property,
      );
    },

    defaultVatRate() {
      // IVA de la reserva: manda el snapshot congelado (reservas.tax_rate, el IVA
      // del anuncio con que se creó); el emisor es solo respaldo para reservas
      // viejas sin snapshot. Así los cargos muestran el mismo IVA que el recibo y
      // la factura, no el del emisor.
      const snap = this.reservationData?.tax_rate;
      if (snap !== null && snap !== undefined && snap !== '') {
        const s = parseFloat(snap);
        if (!Number.isNaN(s)) return s;
      }
      const r = this.reservationData?.billing_emisor?.default_tax_rate
        ?? this.$store?.state?.userRoot?.billing_profile?.default_tax_rate;
      return r ? parseFloat(r) : 0;
    },

    financialLines() {
      return Array.isArray(this.reservationData?.reservation_financial_lines)
        ? this.reservationData.reservation_financial_lines : [];
    },

    useFinancialLines() {
      return this.financialLines.some(l =>
        ['room_night', 'room_stay', 'discount', 'service', 'fee', 'extra_guest', 'tax', 'city_tax', 'adjustment'].includes(String(l?.category || '').toLowerCase()) &&
        parseFloat(l?.amount || 0) !== 0
      );
    },

    discountRows() {
      if (!this.useFinancialLines) return [];
      return this.financialLines
        .filter(l => String(l?.category || '').toLowerCase() === 'discount' && parseFloat(l?.amount || 0) !== 0)
        .map(l => ({
          _isDiscount: true,
          description: (l.room_name ? l.room_name + ' · ' : '') + (l.description || this.$t('bookingCharges.chips.discount')),
          date: l.service_date || null,
          total_with_vat: parseFloat(l.amount || 0),
        }));
    },

    automaticChargeRows() {
      if (!this.useFinancialLines) return [];

      const labelMap = {
        service: this.$t('bookingCharges.autoCategory.service'),
        fee: this.$t('bookingCharges.autoCategory.fee'),
        extra_guest: this.$t('bookingCharges.autoCategory.extraGuest'),
        tax: this.$t('bookingCharges.autoCategory.tax'),
        city_tax: this.$t('bookingCharges.autoCategory.cityTax'),
        adjustment: this.$t('bookingCharges.autoCategory.adjustment'),
      };

      return this.financialLines
        .filter(l => {
          const category = String(l?.category || '').toLowerCase();
          const amount = parseFloat(l?.amount || 0);
          return ['service', 'fee', 'extra_guest', 'tax', 'city_tax', 'adjustment'].includes(category)
            && amount !== 0
            && !Boolean(l?.is_included);
        })
        .sort((a, b) => {
          const sortA = Number(a?.sort_order || 0);
          const sortB = Number(b?.sort_order || 0);
          if (sortA !== sortB) return sortA - sortB;
          return String(a?.service_date || '').localeCompare(String(b?.service_date || ''));
        })
        .map(l => {
          const quantity = Math.max(1, parseFloat(l?.quantity || 1) || 1);
          const totalWithVat = parseFloat(l?.amount || 0);
          const explicitUnitAmount = l?.unit_amount !== null && l?.unit_amount !== undefined
            ? parseFloat(l.unit_amount)
            : null;
          const unitBruto = explicitUnitAmount !== null && !Number.isNaN(explicitUnitAmount)
            ? explicitUnitAmount
            : parseFloat((totalWithVat / quantity).toFixed(2));

          /* 🚨 Estas líneas NO van al 0 %. La limpieza, la tasa del anfitrión, el
             huésped extra y los ajustes son accesorios del alojamiento y la factura
             los emite a SU mismo tipo. Enseñarlos aquí al 0 % hacía que el panel y
             la factura dijeran cosas distintas de la misma línea: al extender una
             reserva, el «Ajuste de importe» de 250 € salía al 0 % en pantalla y al
             10 % en el documento.
             El importe guardado lleva el IVA dentro, así que el precio unitario se
             enseña como base, igual que en las noches (227,27 → 250,00). */
          const vatRate = this.defaultVatRate;
          const unitPrice = vatRate > 0
            ? parseFloat((unitBruto / (1 + vatRate / 100)).toFixed(2))
            : unitBruto;

          return {
            _isAutomatic: true,
            _financialLineId: l?.id ?? null,
            description: (l?.room_name ? l.room_name + ' · ' : '') + (l?.description || this.$t('bookingCharges.autoCategory.autoFallback')),
            category: labelMap[String(l?.category || '').toLowerCase()] || this.$t('bookingCharges.autoCategory.fee'),
            chargeBasis: this.chargeBasisLabel(l?.meta),
            charge_date: l?.service_date || null,
            quantity,
            unit_price: unitPrice,
            vat_rate: vatRate,
            total_with_vat: totalWithVat,
            currency: l?.currency || this.propertyCurrency,
            is_billed: true,
            source: l?.source || 'financial_line',
          };
        });
    },

    virtualRows() {
      if (this.useFinancialLines) {
        const vat  = this.defaultVatRate;
        const name = this.reservationData?.propiedad?.nombre || this.reservationData?.propiedad?.name || this.$t('bookingCharges.chips.lodging');
        return this.financialLines
          .filter(l => ['room_night', 'room_stay'].includes(String(l?.category || '').toLowerCase()) && parseFloat(l?.amount || 0) !== 0)
          .map(l => {
            const totalWithVat = parseFloat(l.amount || 0);
            // CANTIDAD = nº de habitaciones de la línea (compuesta); el precio unitario
            // es POR habitación (p.ej. 2 Duplex: 2 × 91 base + 10% = 200).
            const qty          = Math.max(1, parseFloat(l.quantity || 1) || 1);
            const baseTotal    = vat > 0 ? (totalWithVat / (1 + vat / 100)) : totalWithVat;
            const priceBase    = parseFloat((baseTotal / qty).toFixed(2));
            return {
              // En hotel, cada noche muestra SU habitación (Duplex/Simple).
              description: l.room_name || name,
              date: l.service_date ? moment(l.service_date).format('DD/MM/YYYY') : '',
              quantity: qty, unit_price: priceBase, vat_rate: vat, total_with_vat: totalWithVat,
            };
          });
      }
      /* 🚨 Agrupado POR HABITACIÓN, como un PMS, no una fila por noche.
         Antes salía Simple-Duplex-Simple-Duplex intercalado por fecha y costaba
         seguir qué había pagado cada habitación. Ahora cada tipo es UNA fila con sus
         noches resumidas, y la cantidad dice la verdad: noches × habitaciones.

         La factura sí lleva el detalle noche a noche —eso lo pide Hacienda—; la
         ficha no tiene por qué ser un listado largo. */
      const rd = this.reservationData;
      if (!rd?.fecha_ingreso || !rd?.fecha_salida) return [];

      const nights = moment(rd.fecha_salida).diff(moment(rd.fecha_ingreso), 'days');
      if (nights <= 0) return [];

      const vat = this.defaultVatRate;
      const nombreDelHotel = rd.propiedad?.nombre || rd.propiedad?.name
        || this.$t('bookingCharges.chips.lodging');

      const lineas = Array.isArray(rd.group_lines) && rd.group_lines.length
        ? rd.group_lines
        : [{ unit_name: null, price: rd.price, rooms: 1, rooms_detail: [] }];

      return lineas
        .map((linea) => {
          const total = parseFloat(linea.price || 0);
          if (total <= 0) return null;

          /* Solo el NÚMERO en el distintivo: el tipo ya está de título y repetirlo
             («H1 – Hab. Simple» bajo «Hab. Simple») solo gasta sitio y se corta. */
          const codigos = (linea.rooms_detail || [])
            .map((h) => h.code || h.name)
            .filter(Boolean);

          const habitaciones = Math.max(1, parseInt(linea.rooms, 10) || codigos.length || 1);
          const unidades = nights * habitaciones;
          const base = vat > 0 ? total / (1 + vat / 100) : total;

          return {
            description: linea.unit_name || nombreDelHotel,
            room_codes: codigos,
            nights,
            rooms: habitaciones,
            date: moment(rd.fecha_ingreso).format('DD/MM/YYYY')
              + ' – ' + moment(rd.fecha_salida).format('DD/MM/YYYY'),
            quantity: unidades,
            unit_price: parseFloat((base / unidades).toFixed(2)),
            vat_rate: vat,
            total_with_vat: total,
          };
        })
        .filter(Boolean);
    },

    allRows() { return [...this.virtualRows, ...this.discountRows, ...this.automaticChargeRows, ...this.charges]; },

    grandTotal() {
      const nightsTotal   = this.virtualRows.reduce((a, r) => a + r.total_with_vat, 0);
      const discountTotal = this.discountRows.reduce((a, r) => a + r.total_with_vat, 0);
      const automaticTotal = this.automaticChargeRows.reduce((a, r) => a + r.total_with_vat, 0);
      const manualTotal   = this.charges.reduce((a, c) => a + parseFloat(c.total_with_vat || 0), 0);
      return parseFloat((nightsTotal + discountTotal + automaticTotal + manualTotal).toFixed(2));
    },

    // ── Multi-factura: agrupar cargos por factura ────────────────────────────
    emittedInvoiceIds() { return this.emittedInvoices.map(i => i.id); },

    // El alojamiento (y los cargos base) ya están facturados si hay ≥1 factura viva emitida.
    lodgingBilled() {
      return this.emittedInvoices.some(i => esFactura(i) && ['sent', 'corrected'].includes(i.status));
    },

    // La factura que "lleva" el alojamiento = la más antigua viva (menor id).
    lodgingBearerId() {
      const live = this.emittedInvoices.filter(i => esFactura(i) && i.status !== 'voided');
      if (!live.length) return null;
      return live.reduce((min, i) => (i.id < min ? i.id : min), live[0].id);
    },

    // Cargos manuales que NO están en una factura emitida (pendientes o en el borrador
    // en curso) → son los que formarán la PRÓXIMA factura.
    workingCharges() {
      return this.charges.filter(c => !c.invoice_id || !this.emittedInvoiceIds.includes(c.invoice_id));
    },

    // Filas base (alojamiento/descuentos/automáticos): se muestran solo mientras NO se
    // haya emitido la primera factura; después van al detalle de esa factura (clúster).
    showBaseRows() { return !this.lodgingBilled; },
    displayCharges() { return this.lodgingBilled ? this.workingCharges : this.charges; },

    hasDisplayRows() {
      if (this.showBaseRows) {
        return this.virtualRows.length || this.discountRows.length
          || this.automaticChargeRows.length || this.charges.length;
      }
      return this.displayCharges.length;
    },

    footerTotal() {
      if (!this.lodgingBilled) return this.grandTotal;
      return parseFloat(this.displayCharges.reduce((a, c) => a + parseFloat(c.total_with_vat || 0), 0).toFixed(2));
    },

    // Facturas emitidas con sus conceptos (clúster) para el listado de arriba.
    emittedWithConcepts() {
      return this.emittedInvoices.map(inv => ({ ...inv, concepts: this.conceptsFor(inv) }));
    },

    /* Conceptos del BORRADOR en curso, para que se puedan revisar ANTES de emitir
       (emitir quema número: si no se ven las líneas, no hay vuelta atrás).
       Misma fuente que las emitidas → `conceptsFor`, en modo borrador. */
    draftConcepts() {
      if (!this.hasActiveDraft) return [];
      return this.conceptsFor(this.activeInvoice, { asDraft: true });
    },

    // ¿Hay un borrador/factura EN CURSO? (la publica el panel por state-changed)
    hasActiveDraft() {
      return !!this.activeInvoice
        && ['draft', 'pending', 'processing', 'error'].includes(this.activeInvoice.status);
    },

    // Sección de "Cargos": visible al preparar la 1ª factura, o mientras se construye
    // otra (hay borrador). Tras emitir, se COLAPSA hasta que el usuario decide crear otra
    // (el panel muestra el botón "Crear otra factura para esta reserva").
    showChargesSection() {
      return !this.lodgingBilled || this.hasActiveDraft;
    },

    chargePreviewTotal() {
      const q = parseFloat(this.chargeModal.form.quantity)   || 0;
      const p = parseFloat(this.chargeModal.form.unit_price) || 0;
      const v = parseFloat(this.chargeModal.form.vat_rate)   || 0;
      return parseFloat(((q * p) * (1 + v / 100)).toFixed(2));
    },

    filteredSaved() {
      const q = this.descSearch.trim().toLowerCase();
      return this.templateGroups
        .map(g => ({ ...g, items: q ? g.items.filter(i => i.name.toLowerCase().includes(q)) : g.items }))
        .filter(g => g.items.length);
    },
  },

  watch: {
    reservaId: { immediate: true, handler(v) { if (v) this.init(); } },
  },

  mounted()       { document.addEventListener('click', this.onDocClick); },
  beforeDestroy() { document.removeEventListener('click', this.onDocClick); },

  methods: {
    async init() {
      this.loading = true;
      try { await Promise.all([this.fetchCharges(), this.fetchTemplates()]); }
      finally { this.loading = false; }
    },

    async fetchCharges() {
      // De paso, qué habitaciones hay: si solo hay una, el selector ni se enseña.
      axios.get(`/reservas/${this.reservaId}/billable-rooms`)
        .then(({ data }) => { this.habitaciones = { splittable: !!data.splittable, rooms: data.rooms || [] }; })
        .catch(() => { this.habitaciones = { splittable: false, rooms: [] }; });

      const r = await axios.get(`/booking/${this.reservaId}/charges`);
      this.charges = r.data?.data ?? r.data ?? [];
    },

    // ─── FIX: cuando la factura se anula, refrescar cargos Y el panel ─────
    async onInvoiceVoided() {
      await this.fetchCharges();
      this.$refs.invoicePanel?.loadInvoice?.();
    },

    // Cuando se crea una factura directamente, refrescar cargos
    async onInvoiceCreated() {
      await this.fetchCharges();
    },

    // Modal cargo
    openChargeModal(charge = null) {
      this.showDropdown = false; this.descSearch = '';
      if (charge) {
        this.chargeModal = { open: true, isNew: false, editId: charge.id,
          form: { charge_date: charge.charge_date, description: charge.description, category: charge.category || '', quantity: charge.quantity, unit_price: charge.unit_price, vat_rate: charge.vat_rate, notes: charge.notes || '', room_instance_id: charge.room_instance_id || null } };
      } else {
        this.chargeModal = { open: true, isNew: true, editId: null, form: BLANK_FORM(this.propertyCurrency, this.defaultVatRate) };
      }
    },

    closeChargeModal() { if (this.saving) return; this.chargeModal.open = false; this.showDropdown = false; },

    async saveCharge() {
      const f = this.chargeModal.form;
      if (!f.description?.trim()) { toastr.warning(this.$t('bookingCharges.toast.descRequired'), this.$t('bookingCharges.toast.warningTitle')); return; }
      this.saving = true;
      try {
        // `room_instance_id` nulo = el cargo es de toda la reserva, como siempre.
        const payload = { charge_date: f.charge_date, description: f.description, category: f.category || null, quantity: f.quantity, unit_price: f.unit_price, vat_rate: f.vat_rate, currency: this.propertyCurrency, notes: f.notes || null, room_instance_id: f.room_instance_id || null };
        if (this.chargeModal.isNew) { await axios.post(`/booking/${this.reservaId}/charges`, payload); toastr.success(this.$t('bookingCharges.toast.chargeAdded'), this.$t('bookingCharges.toast.doneTitle')); }
        else { await axios.put(`/charges/${this.chargeModal.editId}`, payload); toastr.success(this.$t('bookingCharges.toast.chargeUpdated'), this.$t('bookingCharges.toast.doneTitle')); }
        this.chargeModal.open = false;
        await this.fetchCharges();
        // Refrescar el borrador de factura: su total incluye los cargos
        this.$refs.invoicePanel?.loadInvoice?.();
        // Notificar al padre para refrescar el importe de la reserva y el link de pago
        this.$emit('charges-changed');
      } catch (e) { toastr.error(e.response?.data?.message || this.$t('bookingCharges.toast.saveFailed'), this.$t('bookingCharges.toast.errorTitle')); }
      finally { this.saving = false; }
    },

    async deleteCharge(charge) {
      this.deleting = charge.id; this.pendingDelete = null;
      try { await axios.delete(`/charges/${charge.id}`); toastr.success(this.$t('bookingCharges.toast.chargeDeleted'), this.$t('bookingCharges.toast.doneTitle')); await this.fetchCharges(); this.$refs.invoicePanel?.loadInvoice?.(); this.$emit('charges-changed'); }
      catch (e) { toastr.error(e.response?.data?.message || this.$t('bookingCharges.toast.deleteFailed'), this.$t('bookingCharges.toast.errorTitle')); }
      finally { this.deleting = null; }
    },

    async fetchTemplates() {
      const pid = this.reservationData?.propiedad?.id || this.reservationData?.propiedad_id;
      try {
        const r = await axios.get('/booking/charge-templates', { params: { property_id: pid } });
        this.allTemplates = r.data.templates ?? []; this.templateGroups = r.data.grouped ?? [];
      } catch { /* silencioso */ }
    },

    async saveTpl() {
      if (!this.newTpl.name?.trim()) return;
      this.savingTpl = true;
      try {
        const pid = this.reservationData?.propiedad?.id || this.reservationData?.propiedad_id;
        await axios.post('/booking/charge-templates', { ...this.newTpl, property_id: pid || null, currency: this.propertyCurrency });
        toastr.success(this.$t('bookingCharges.toast.chargeSaved'), this.$t('bookingCharges.toast.doneTitle')); this.newTpl = BLANK_TPL(); await this.fetchTemplates();
      } catch { toastr.error(this.$t('bookingCharges.toast.saveFailed'), this.$t('bookingCharges.toast.errorTitle')); }
      finally { this.savingTpl = false; }
    },

    async deleteTpl(id) {
      this.deletingTpl = id;
      try { await axios.delete(`/booking/charge-templates/${id}`); toastr.success(this.$t('bookingCharges.toast.chargeDeleted'), this.$t('bookingCharges.toast.doneTitle')); await this.fetchTemplates(); }
      catch { toastr.error(this.$t('bookingCharges.toast.deleteFailed'), this.$t('bookingCharges.toast.errorTitle')); }
      finally { this.deletingTpl = null; }
    },

    openDropdown()     { this.descSearch = ''; this.showDropdown = true; },
    onDescInput()      { this.descSearch = this.chargeModal.form.description || ''; if (!this.showDropdown) this.showDropdown = true; },
    applyTemplate(tpl) { this.chargeModal.form.description = tpl.name; this.chargeModal.form.category = tpl.category || ''; this.chargeModal.form.unit_price = tpl.default_price; this.chargeModal.form.vat_rate = tpl.default_vat; this.showDropdown = false; },
    onDocClick(e)      { const g = this.$refs.descGroup; if (g && !g.contains(e.target)) this.showDropdown = false; },
    openManageModal()  { this.showDropdown = false; this.newTpl = BLANK_TPL(); this.showManageModal = true; this.$nextTick(() => this.$refs.newTplInput?.focus()); },

    fmtParts(amount, compact = false) {
      const n    = parseFloat(amount) || 0;
      const code = this.propertyCurrency || 'USD';
      const commaLocales = ['COP','EUR','ARS','CLP','PEN','BRL'];
      const codeOnly     = ['COP','ARS','CLP','PEN'];
      const useComma     = commaLocales.includes(code);
      const showCode     = codeOnly.includes(code);
      let formatted;
      if (compact) { formatted = Math.round(n).toLocaleString(useComma ? 'es-CO' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
      else         { formatted = n.toLocaleString(useComma ? 'es-CO' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
      return { code, amount: formatted, showCode };
    },
    fmt(amount)   { const p = this.fmtParts(amount, false); return `${p.code} ${p.amount}`; },
    formatDate(d) { return d ? moment(d).format('DD/MM/YYYY') : ''; },

    // Conceptos ya facturados en ESTA factura (para el detalle/clúster del listado):
    // la que lleva el alojamiento suma alojamiento + descuentos + automáticos; todas
    // suman sus cargos manuales vinculados.
    /**
     * @param {object} inv
     * @param {{ asDraft?: boolean }} [opts] `asDraft` para la factura EN CURSO
     *        (borrador / emitiéndose / error), que no está en `emittedInvoices`.
     *
     * 🚨 PORTADOR DEL ALOJAMIENTO EN UN BORRADOR — decidido a propósito:
     * `lodgingBearerId` es el id MÍNIMO de las facturas ya ASENTADAS vivas, y el
     * borrador nunca está en ese conjunto (el panel separa asentadas de «en curso»).
     * Por eso `inv.id === lodgingBearerId` es SIEMPRE falso para un borrador: si lo
     * usáramos tal cual, las noches no saldrían NUNCA en su detalle.
     *
     * El borrador se considera portador del alojamiento cuando NO hay ninguna
     * factura viva emitida (`lodgingBearerId === null`), que es el mismo invariante
     * que ya manda en la tabla de cargos (`showBaseRows = !lodgingBilled`) y en
     * `footerTotal`: mientras no se ha emitido nada, el alojamiento, los descuentos y
     * los automáticos son de la PRÓXIMA factura, o sea de este borrador. En cuanto
     * hay una emitida, el alojamiento ya está facturado por ella y el borrador solo
     * lleva los cargos manuales pendientes.
     *
     * Y los cargos manuales de un borrador son `workingCharges`, no
     * `charges.filter(invoice_id === inv.id)`: un cargo pendiente puede seguir con
     * `invoice_id` nulo y aun así contar en el total del borrador. Así la suma de los
     * conceptos cuadra con `footerTotal` por construcción.
     */
    conceptsFor(inv, { asDraft = false } = {}) {
      const rows = [];
      const carriesLodging = asDraft
        ? this.lodgingBearerId === null
        : inv.id === this.lodgingBearerId;

      if (carriesLodging) {
        this.virtualRows.forEach(r => rows.push({
          description: r.description || this.$t('bookingCharges.chips.lodging'), date: r.date,
          quantity: r.quantity, unit_price: r.unit_price, vat_rate: r.vat_rate,
          total_with_vat: r.total_with_vat,
        }));
        this.automaticChargeRows.forEach(r => rows.push({
          description: r.description, date: r.charge_date ? this.formatDate(r.charge_date) : '',
          quantity: r.quantity, unit_price: r.unit_price, vat_rate: r.vat_rate,
          total_with_vat: r.total_with_vat,
        }));
        this.discountRows.forEach(r => rows.push({
          description: r.description, date: r.date ? this.formatDate(r.date) : '',
          /* Un descuento viene como un importe suelto en `financial_lines`: no tiene
             cantidad, precio unitario ni IVA propios. Van en nulo para que se pinten
             como «—», igual que en la tabla de cargos, en vez de inventarse un
             «1 × importe» que nadie puede cuadrar. */
          quantity: null, unit_price: null, vat_rate: null,
          total_with_vat: r.total_with_vat, discount: true,
        }));
      }

      const manuales = asDraft
        ? this.workingCharges
        : this.charges.filter(c => c.invoice_id === inv.id);

      manuales.forEach(c => rows.push({
        description: c.description, date: c.charge_date ? this.formatDate(c.charge_date) : '',
        quantity: parseFloat(c.quantity || 1), unit_price: parseFloat(c.unit_price || 0),
        vat_rate: c.vat_rate, total_with_vat: parseFloat(c.total_with_vat || 0),
      }));
      return rows;
    },
    chargeBasisLabel(meta) {
      if (!meta) return '';
      if (meta.value_type === 'percent') return this.$t('bookingCharges.chargeBasis.percentOfTotal');
      switch (meta.charge_mode) {
        case 'per_night': return this.$t('bookingCharges.chargeBasis.perNight');
        case 'per_person': return this.$t('bookingCharges.chargeBasis.perPerson');
        case 'per_person_per_night': return this.$t('bookingCharges.chargeBasis.perPersonPerNight');
        case 'per_stay': return this.$t('bookingCharges.chargeBasis.perStay');
        default: return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$brand:  #5e71e4;
$ink:    #0f172a;
$ink2:   #334155;
$ink3:   #64748b;
$ink4:   #94a3b8;
$ink5:   #cbd5e1;
$ink6:   #e2e8f0;
$ink7:   #f1f5f9;
$ink8:   #f8fafc;
$red:    #dc2626;
$green:  #16a34a;
$r:      10px;
$rs:     7px;

/* ══ Módulo ═══════════════════════════════════════════════════════════════════ */
.rc { display: flex; flex-direction: column; gap: 16px; font-family: 'DM Sans', system-ui, sans-serif; }

/* ── Header ─────────────────────────────────────────────────────────────────── */
.rc-header {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.rc-header__title {
  // !important: el global `p { Inter !important }` (App.scss) lo pisaba.
  margin: 0; font-size: .95rem; font-weight: 700; color: $ink;
  font-family: 'DM Sans', sans-serif !important; letter-spacing: -.01em;
}
.rc-header__sub {
  margin: 0; font-size: 13px; color: $ink3;
  font-family: 'DM Sans', sans-serif !important;
}

/* Botón Añadir cargo */
.rc-btn-add {
  display: inline-flex; align-items: center; gap: 6px;
  background: $brand; color: #fff; border: none; border-radius: $rs;
  padding: 8px 14px; font-size: .82rem; font-weight: 600; cursor: pointer;
  transition: all .18s; box-shadow: 0 2px 8px rgba($brand,.24); white-space: nowrap;
  &:hover:not(:disabled) { background: darken($brand,8%); transform: translateY(-1px); }
  &:disabled { opacity: .45; cursor: not-allowed; transform: none; box-shadow: none; }
  &--ghost { background: transparent; color: $brand; border: 1.5px solid $brand; box-shadow: none; &:hover:not(:disabled) { background: rgba($brand,.06); } }
}

/* ── Loading / Empty ─────────────────────────────────────────────────────────── */
.rc-loading { display: flex; align-items: center; gap: 8px; color: $ink3; font-size: .84rem; }
.rc-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid $ink6; border-top-color: $brand; border-radius: 50%; animation: rc-spin .7s linear infinite; }

.rc-empty {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  padding: 20px; background: $ink8; border: 1.5px dashed $ink6;
  border-radius: $r;
}
.rc-empty__icon {
  width: 40px; height: 40px; background: $ink7; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: $ink4;
  font-size: 18px;
}
.rc-empty__title { margin: 0 0 2px; font-size: .88rem; font-weight: 700; color: $ink2; font-family: 'DM Sans', sans-serif; }
.rc-empty__sub   { margin: 0; font-size: .78rem; color: $ink3; }

/* ══ TABLA ════════════════════════════════════════════════════════════════════ */
.rc-table {
  background: #fff; border: 1px solid $ink6; border-radius: $r; overflow: hidden;
}

/* Cabecera */
.rc-table__head {
  display: grid;
  grid-template-columns: 1fr 52px 80px 60px 90px 72px;
  align-items: center; padding: 7px 16px;
  background: $ink8; border-bottom: 1px solid $ink6;
}
.rth-desc { font-family: 'DM Sans', sans-serif; font-size: .65rem; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: $ink4; }
.rth-val  { font-family: 'DM Sans', sans-serif; font-size: .65rem; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: $ink4; text-align: right; &--total { color: $ink3; } }
.rth-actions { width: 72px; }

/* ── Fila ─────────────────────────────────────────────────────────────────── */
.rc-row {
  display: grid;
  grid-template-columns: 1fr 52px 80px 60px 90px 72px;
  align-items: center; padding: 12px 16px;
  border-bottom: 1px solid $ink7; transition: background .12s;

  &:last-of-type { border-bottom: none; }
  &:hover:not(.rc-row--lodging) { background: $ink8; }
  &--lodging {
   background: #fcfcfc;
    border-bottom-color: #f3f3f3;
    &:hover { background: #fafafa; }
  }
  &--billed  { opacity: .82; }
}

/* Columna descripción */
.rc-row__desc { min-width: 0; display: flex; flex-direction: column; gap: 3px; padding-right: 10px; }
.rc-row__name {
  font-family: 'DM Sans', sans-serif;
  font-size: .875rem; font-weight: 700; color: $ink;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  letter-spacing: -.01em;
}
.rc-row__meta { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.rc-row__date { font-size: .7rem; color: $ink4; white-space: nowrap; font-variant-numeric: tabular-nums; }
.rc-row__note { font-size: .7rem; color: $ink3; font-style: italic; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 150px; }

/* Chips */
/* El número de habitación, en su propia etiqueta y no pegado al tipo: juntos se
   cortaban y el cargo no decía de qué habitación era. */
.rc-row__meta--sub { margin-top: 2px; }
.rc-row__stay { font-size: 11.5px; color: #8a8f98; }

.rc-chip--room {
  background: #eef0f4;
  color: #4a4e57;
  font-weight: 600;
}

.rc-chip {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: .59rem; font-weight: 700; padding: 1px 6px; border-radius: 99px;
  white-space: nowrap; font-family: 'DM Sans', sans-serif; letter-spacing: .03em;
  color: $brand; background: rgba($brand,.08); border: 1px solid rgba($brand,.15);
  &--blue  { color: #0369a1; background: #eff8ff; border-color: #bae6fd; }
  &--green { color: darken($green,8%); background: #f0fdf4; border-color: #bbf7d0; }
  &--violet { color: #6d28d9; background: #f5f3ff; border-color: #ddd6fe; }
}

/* Columnas de valores */
.rc-row__val {
  text-align: right; display: flex; justify-content: flex-end; align-items: center;
}

/* Precios con moneda */
.rc-price {
  font-family: 'DM Sans', sans-serif;
  font-size: .81rem; font-weight: 500; color: $ink2;
  display: inline-flex; align-items: baseline; gap: 1px;
}
.rc-total {
  font-family: 'DM Sans', sans-serif;
  font-size: .9rem; font-weight: 700; color: $ink;
  display: inline-flex; align-items: baseline; gap: 1px;
}
.rc-cur {
  font-size: .56rem; font-weight: 700; color: $ink4;
  letter-spacing: .02em; text-transform: uppercase; line-height: 1; margin-right: 1px;
  &--code   { font-size: .6rem; color: $ink3; }
  &--footer { font-size: .66rem; color: rgba($brand,.6); &.rc-cur--code { color: rgba($brand,.5); } }
}
.rc-vat {
  font-family: 'DM Sans', sans-serif;
  font-size: .74rem; font-weight: 600; color: $ink3;
}

/* ── Íconos de acción — estilo FA con cuadrito gris profesional ─────────────── */
.rc-row__actions {
  display: flex; align-items: center; justify-content: flex-end; gap: 5px;
}

.rc-icon-btn {
  width: 28px; height: 28px; border-radius: 7px; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; transition: all .15s; flex-shrink: 0;

  /* Base: cuadrito gris claro */
  background: $ink7; color: $ink3; border: 1px solid $ink6;

  &:disabled { opacity: .35; cursor: not-allowed; }

  /* Editar: hover azul suave */
  &--edit {
    &:hover:not(:disabled) {
      background: #eff6ff; color: #2563eb; border-color: #bfdbfe;
    }
  }

  /* Eliminar: hover rojo suave */
  &--del {
    &:hover:not(:disabled) {
      background: #fef2f2; color: $red; border-color: #fecaca;
    }
  }

  /* Candado: solo visual, sin hover interactivo */
  &--lock {
    cursor: default; color: $ink5; background: $ink8; border-color: $ink6;
    font-size: 10px;
  }
}

.rc-lock-ico { display: flex; align-items: center; justify-content: flex-end; width: 100%; }

/* ── Fila descuento ──────────────────────────────────────────────────────── */
.rc-row--discount {
  background: #f0fdf4;
  border-bottom-color: #dcfce7;
  &:hover { background: #e8fdf0; }
}
.rc-row--auto {
  background: #faf8ff;
  border-bottom-color: #ede9fe;
  &:hover { background: #f6f2ff; }
}
.rc-row__name--discount { color: $green; }
.rc-total--discount { color: $green; }

/* Footer total */
.rc-table__foot {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-top: 1.5px solid $ink6; background: $ink8;
}
.rc-foot__label {
  font-family: 'DM Sans', sans-serif;
  font-size: .68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .08em; color: $ink3;
}
.rc-foot__total {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.1rem; font-weight: 700; color: $brand;
  display: flex; align-items: baseline; gap: 2px;
}

/* ── Panel factura ─────────────────────────────────────────────────────────── */
.rc-invoice-section { border-top: 1px solid $ink6; padding-top: 16px; display: flex; flex-direction: column; gap: 10px; }
.rc-invoice-label {
  font-family: 'DM Sans', sans-serif !important;
  font-size: 13px; font-weight: 700; color: $ink; letter-spacing: -.01em;
}

/* ── Botón "Crear factura" ─────────────────────────────────────────────────── */
.rc-create-invoice-row {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 16px; background: rgba($brand,.04);
  border: 1.5px dashed rgba($brand,.25); border-radius: $r;
}
.rc-btn-create-invoice {
  display: inline-flex; align-items: center; gap: 7px;
  background: $brand; color: #fff; border: none; border-radius: $rs;
  padding: 9px 16px; font-size: .85rem; font-weight: 700; cursor: pointer;
  transition: all .18s; box-shadow: 0 2px 10px rgba($brand,.28);
  white-space: nowrap; flex-shrink: 0;
  &:hover:not(:disabled) { background: darken($brand,8%); transform: translateY(-1px); box-shadow: 0 4px 14px rgba($brand,.36); }
  &:disabled { opacity: .45; cursor: not-allowed; transform: none; box-shadow: none; }
  i { font-size: .9rem; }
}
.rc-create-invoice-hint {
  font-size: .78rem; color: $ink3; flex: 1; line-height: 1.4;
}

/* ── Select dentro de formulario ───────────────────────────────────────────── */
.mf-select {
  cursor: pointer;
  padding-right: 32px !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* ══ MODALES ══════════════════════════════════════════════════════════════════ */
.rc-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,.32);
  backdrop-filter: blur(4px); z-index: 9999;
  display: flex; align-items: center; justify-content: center; padding: 20px;
}

.rc-modal {
  background: #fff; border-radius: 14px; width: 100%; max-width: 520px;
  box-shadow: 0 20px 60px rgba(15,23,42,.18); animation: rc-pop .2s ease;
  overflow: hidden; display: flex; flex-direction: column; max-height: 90vh;
  &--sm     { max-width: 380px; }
  &--charge { max-width: 540px; }
}
@keyframes rc-pop { from{transform:scale(.96);opacity:0} to{transform:scale(1);opacity:1} }

.rc-modal__head {
  display: flex; align-items: center; gap: 12px;
  padding: 18px 20px 14px; border-bottom: 1px solid $ink7; flex-shrink: 0;
}
.rc-modal__icon {
  width: 36px; height: 36px; background: rgba($brand,.1); color: $brand;
  border-radius: 9px; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; font-size: 16px;
  &--danger { background: rgba($red,.1); color: $red; }
}
.rc-modal__title {
  margin: 0 0 2px; font-size: .95rem; font-weight: 700; color: $ink;
  font-family: 'DM Sans', sans-serif;
}
.rc-modal__sub   { margin: 0; font-size: .78rem; color: $ink3; }
.rc-modal__x {
  margin-left: auto; background: $ink8; border: 1px solid $ink6; cursor: pointer; color: $ink4;
  width: 30px; height: 30px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  font-size: 13px; transition: all .12s;
  &:hover { background: $ink7; color: $ink2; border-color: $ink5; }
  &:disabled { opacity: .4; cursor: not-allowed; }
}

.rc-modal__body {
  padding: 18px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px;
  &--sm { padding: 14px 20px; }
}
.rc-modal__foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 14px 20px; border-top: 1px solid $ink7; background: $ink8; flex-shrink: 0;
  &--end { justify-content: flex-end; }
}

/* Botones de modal */
.rc-mbtn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: $rs; font-size: .84rem; font-weight: 600;
  cursor: pointer; border: none; transition: all .14s; white-space: nowrap; font-family: inherit;
  &:disabled { opacity: .45; cursor: not-allowed; }
  &--cancel { background: #fff; color: $ink2; border: 1.5px solid $ink6; &:hover:not(:disabled) { border-color: $ink5; background: $ink8; } }
  &--save   { background: $brand; color: #fff; box-shadow: 0 2px 8px rgba($brand,.22); min-width: 120px; justify-content: center; &:hover:not(:disabled) { background: darken($brand,8%); } }
  &--danger { background: $red; color: #fff; &:hover:not(:disabled) { background: darken($red,8%); } }
}

/* ══ FORMULARIO DEL MODAL — FIX: inputs uniformes ════════════════════════════ */
.mf-group {
  display: flex; flex-direction: column; gap: 5px; position: relative;
}

/* FIX: grids consistentes con altura fija en todos los inputs */
.mf-grid {
  display: grid; gap: 12px;
  &--2 { grid-template-columns: 1fr 1fr; }
  &--3 { grid-template-columns: 1fr 1fr 1fr; }
}
.mf-row-wrap { display: flex; gap: 8px; align-items: stretch; }

.mf-label {
  font-family: 'DM Sans', sans-serif;
  font-size: .68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; color: $ink3;
  display: block; margin-bottom: 2px;
}
.mf-opt { font-weight: 400; text-transform: none; letter-spacing: 0; color: $ink4; }
.req    { color: $red; }

/* FIX CLAVE: altura fija y box-sizing border-box en TODOS los inputs */
.mf-input {
  height: 38px !important;          /* altura fija uniforme */
  min-height: 38px !important;
  padding: 0 11px !important;
  border: 1.5px solid $ink6;
  border-radius: $rs;
  font-size: .85rem; color: $ink; background: #fff; outline: none;
  width: 100%; box-sizing: border-box !important; /* crítico */
  font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
  flex: 1;
  /* Quitar estilos nativos de date que afectan altura */
  appearance: none;
  -webkit-appearance: none;
  line-height: 1; /* normaliza diferencias entre tipo text y date */

  &:focus { border-color: $brand; box-shadow: 0 0 0 3px rgba($brand,.1); }

  /* Override para type=number: quitar flechas que afectan el tamaño */
  &[type=number] {
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }

  /* Override para type=date */
  &[type=date] {
    padding-right: 8px !important;
    cursor: pointer;
  }
}

.mf-gear {
  flex-shrink: 0;
  width: 38px; height: 38px;  /* mismo alto que mf-input */
  box-sizing: border-box;
  border: 1.5px solid $ink6;
  border-radius: $rs; background: #fff; color: $ink4; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; transition: all .14s;
  &:hover { border-color: $brand; color: $brand; background: rgba($brand,.05); }
}

.mf-preview {
  display: flex; align-items: center; justify-content: space-between;
  background: rgba($brand,.05); border: 1px solid rgba($brand,.15);
  border-radius: $rs; padding: 10px 14px;
}
.mf-preview__lbl {
  font-family: 'DM Sans', sans-serif;
  font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: $ink3;
}
.mf-preview__val {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.1rem; font-weight: 700; color: $brand;
  display: flex; align-items: baseline; gap: 2px;
}

/* ── Dropdown de plantillas ──────────────────────────────────────────────── */
.art-dd {
  position: absolute; top: calc(100% + 4px); left: 0;
  min-width: 100%; max-height: 280px; overflow-y: auto;
  background: #fff; border: 1px solid $ink6; border-radius: $r;
  box-shadow: 0 10px 36px rgba(15,23,42,.14); z-index: 600; animation: rc-up .16s ease;
  &::-webkit-scrollbar { width: 5px; } &::-webkit-scrollbar-thumb { background: $ink6; border-radius: 3px; }
}
.art-dd__head    { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px 8px; }
.art-dd__ttl     { font-family: 'DM Sans', sans-serif; font-size: .65rem; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: $ink4; }
.art-dd__manage  { display: inline-flex; align-items: center; gap: 4px; background: rgba($brand,.1); color: $brand; border: none; border-radius: 5px; padding: 3px 9px; font-size: .7rem; font-weight: 700; cursor: pointer; transition: all .12s; &:hover { background: rgba($brand,.18); } }
.art-dd__sep     { height: 1px; background: $ink7; }
.art-dd__empty   { padding: 14px; font-size: .8rem; color: $ink4; text-align: center; }
.art-dd__link    { color: $brand; cursor: pointer; font-weight: 600; text-decoration: underline; }
.art-dd__cat     { padding: 6px 14px 2px; font-family: 'DM Sans', sans-serif; font-size: .63rem; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: $ink4; }
.art-dd__item    { display: flex; align-items: center; justify-content: space-between; padding: 9px 14px; cursor: pointer; transition: background .1s; &:hover { background: $ink8; } }
.art-dd__item-name { font-size: .85rem; color: $ink; font-weight: 500; }
.art-dd__item-right { display: flex; align-items: center; gap: 8px; }
.art-dd__vat     { font-size: .7rem; color: $ink4; background: $ink7; padding: 1px 6px; border-radius: 4px; }
.art-dd__price   { font-family: 'DM Sans',sans-serif; font-size: .8rem; color: $ink3; font-weight: 600; }

/* ── Modal gestionar plantillas ──────────────────────────────────────────── */
.rc-tpl-form { padding: 14px 20px; border-bottom: 1px solid $ink7; flex-shrink: 0; display: flex; flex-direction: column; gap: 10px; }
.rc-tpl-grid { display: flex; gap: 8px; flex-wrap: wrap; }
.rc-tf { display: flex; flex-direction: column; gap: 4px; &--name { flex: 2; min-width: 120px; } &--cat { flex: 1; min-width: 90px; } &--price { flex: 0 0 95px; } &--vat { flex: 0 0 70px; } }

.rc-tpl-list { flex: 1; overflow-y: auto; padding: 6px 0; &::-webkit-scrollbar { width: 5px; } &::-webkit-scrollbar-thumb { background: $ink6; border-radius: 3px; } }
.rc-tpl-empty { padding: 20px; text-align: center; font-size: .82rem; color: $ink4; }
.rc-tpl-group { }
.rc-tpl-cat   { padding: 8px 20px 3px; font-family: 'DM Sans', sans-serif; font-size: .63rem; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: $ink4; }
.rc-tpl-item  { display: flex; align-items: center; justify-content: space-between; padding: 9px 20px; border-bottom: 1px solid $ink7; transition: background .1s; &:last-child { border-bottom: none; } &:hover { background: $ink8; } }
.rc-tpl-item__info { display: flex; align-items: center; gap: 10px; }
.rc-tpl-item__name  { font-size: .86rem; font-weight: 600; color: $ink; }
.rc-tpl-item__price { font-family: 'DM Sans',sans-serif; font-size: .82rem; font-weight: 600; color: $ink3; }
.rc-tpl-item__vat   { font-size: .7rem; color: $ink4; background: $ink7; padding: 2px 6px; border-radius: 4px; }
.rc-tpl-del {
  width: 30px; height: 30px; border-radius: 7px; border: 1px solid $ink6;
  background: $ink8; color: $ink4;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 12px; transition: all .12s; flex-shrink: 0;
  &:hover:not(:disabled) { background: #fef2f2; color: $red; border-color: #fecaca; }
  &:disabled { opacity: .4; cursor: not-allowed; }
}

/* ── Eliminar cargo ────────────────────────────────────────────────────────── */
.rc-del-name { margin: 0 0 4px; font-size: .9rem; color: $ink; }
.rc-del-meta { margin: 0; font-size: .82rem; color: $ink3; }

/* ── Animaciones ─────────────────────────────────────────────────────────── */
@keyframes rc-spin { to { transform: rotate(360deg); } }
@keyframes rc-pop  { from{transform:scale(.96);opacity:0} to{transform:scale(1);opacity:1} }
@keyframes rc-up   { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }

.rc-fade-enter-active, .rc-fade-leave-active { transition: opacity .18s ease; }
.rc-fade-enter, .rc-fade-leave-to { opacity: 0; }

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .rc-table__head { display: none; }
  .rc-row {
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    gap: 4px;
    .rc-row__desc { grid-column: 1; grid-row: 1; }
    .rc-row__actions { grid-column: 2; grid-row: 1; }
    .rc-row__val { display: none; }
  }
  .mf-grid--2 { grid-template-columns: 1fr; }
  .mf-grid--3 { grid-template-columns: 1fr 1fr; }
  .rc-tpl-grid { flex-direction: column; }
  .rc-tf { flex: none; width: 100%; }
  .rc-modal__foot { flex-direction: column-reverse; }
  .rc-mbtn { width: 100%; justify-content: center; }
}
</style>
